import Vue from 'vue'
import store from '@/store'
import msgpack from 'msgpack-lite'
import sha256 from 'js-sha256'
import $ from 'jquery'

const api = {
    version: 20190607,
    callerId: null,
    onBeforeRequestCallback: null,
    onResponseSuccessCallback: null,
    onResponseErrorCallback: null,
    httpGlobalOptions: {},
    apiHeadersInUrl: {
        'Authorization': 'x-api-auth',
        'X-Signature': 'x-api-sig',
        'X-Nonce': 'x-api-nonce',
        'X-Caller-Id': 'x-api-caller'
    },
    
    CONTENT_TYPE_FORM: 1,
    CONTENT_TYPE_BODY: 2,
    CONTENT_TYPE_MULTI_PART: 3,
    
    install(Vue, options) {
        options = options || {}
        
        if(options.callerId)
            this.callerId = options.callerId
        
        if($.isFunction(options.onBeforeRequestCallback))
            this.onBeforeRequestCallback = options.onBeforeRequestCallback
        
        if($.isFunction(options.onResponseSuccessCallback))
            this.onResponseSuccessCallback = options.onResponseSuccessCallback
        
        if($.isFunction(options.onResponseErrorCallback))
            this.onResponseErrorCallback = options.onResponseErrorCallback
        
        if(options.httpGlobalOptions)
            this.httpGlobalOptions = options.httpGlobalOptions
        
        if(options.apiHeadersInUrl)
            this.apiHeadersInUrl = $.extend(this.apiHeadersInUrl, options.apiHeadersInUrl)
        
        Vue.prototype.$api = Vue.$api = this
    },
    
    getNonce(required) {
        let wait = $.Deferred()
        
        if(!required)
            return wait.resolve(false).promise()
        
        if(store.state.api.nonces.length) {
            let payload = {
                cmd: 'update'
            }
            
            store.dispatch('api/setOrUpdateNonces', payload)
            
            return wait.resolve(payload.nonce[0]).promise()
        }
        
        this.get('/getNonce', {
            total: 50
        }).then(
            (response) => {
                let nonce = response.data.nonces.slice(0, 1)
                
                store.dispatch('api/setOrUpdateNonces', {
                    cmd: 'set',
                    nonces: response.data.nonces.slice(1)
                })
                
                return wait.resolve(nonce[0]).promise()
            },
            (response) => {
                return wait.reject(response).promise()
            }
        )
        
        return wait.promise()
    },
    
    get(path, data = {}, nonceRequired = false, options = {}) {
        return this._run('get', path, data, nonceRequired, options)
    },
    
    post(path, data = {}, nonceRequired = false, options = {}, contentType = 1) {
        return this._run('post', path, data, nonceRequired, options, contentType)
    },
    
    put(path, data = {}, nonceRequired = false, options = {}, contentType = 1) {
        return this._run('put', path, data, nonceRequired, options, contentType)
    },
    
    delete(path, data = {}, nonceRequired = false, options = {}, contentType = 1) {
        return this._run('delete', path, data, nonceRequired, options, contentType)
    },
    
    getSignature(data, key) {
        return sha256.hmac(key || store.state.api.clientKey, data)
    },
    
    getError(response, includeReqId = false) {
        if(typeof response !== 'object')
            return response
        
        let errMsg = ''
        
        if(typeof response.data['errorType'] != 'undefined') {
            // eslint-disable-next-line curly
            if(
                response.data.errorType === 'API_ERROR'
                    &&
                response.data.errorCode === 'BLOCKED_DUE_TO_LIMITS'
            ) {
                errMsg = "You've reached request limits, please retry after "+response.headers['x-api-request-reset']+' seconds.'
            }
            else
                errMsg = response.data.errorMessage
        }
        
        if(!includeReqId)
            return errMsg
        
        return errMsg+' [Request ID: '+response.headers['x-api-request-id']+']'
    },
    
    _run(method, path, data, nonceRequired, givenOptions, contentType = 1, internalReTry = false) {
        let wait = $.Deferred()
        
        this.getNonce(nonceRequired).then((nonce) => {
            let api = store.state.api
            let format = givenOptions.format || api.format
            
            let options = $.extend(true, {}, {
                url: api.baseUrl+'/'+format+path,
                method: method,
                headers: {
                    'Accept': format === 'msgpack' ?'application/x-msgpack' :'application/json'
                },
                params: (method === 'get' || method === 'delete') ?data :{},
                data: (method !== 'get' && method !== 'delete') ?data :'',
                timeout: 300000,
                withCredentials: false,
                responseType: format === 'msgpack' ?'arraybuffer' :'json',
                paramsSerializer: (params) => {
                    return $.param(params)
                }
            }, this.httpGlobalOptions, givenOptions)
            
            if(api.maintenance && api.maintenance.apiAccessCode)
                options.headers['X-InterimAccess'] = api.maintenance.apiAccessCode
            
            if(this.callerId !== null)
                options.headers['X-Caller-Id'] = this.callerId
            
            if(contentType === this.CONTENT_TYPE_FORM) {
                options.headers['Content-Type'] = 'application/x-www-form-urlencoded'
                
                if($.type(data) === 'object')
                    options.data = $.param(data)
            }
            else if(contentType === this.CONTENT_TYPE_BODY) {
                options.headers['Content-Type'] = format === 'msgpack' ?'application/x-msgpack' :'application/json'
                
                if($.type(data) === 'object')
                    options.data = format === 'msgpack' ?msgpack.encode(data) :JSON.stringify(data)
            }
            
            if(api.token.length) {
                options.headers['Authorization'] = api.tokenType + ' ' + api.token
                
                if(nonce !== false)
                    options.headers['X-Nonce'] = nonce
                
                if(api.clientKey.length) {
                    let hash = options.data
                    
                    if(method === 'get' || method === 'delete' || contentType === this.CONTENT_TYPE_MULTI_PART)
                        hash = $.type(options.params) === 'object' ?$.param(options.params) :options.params
                    
                    hash = sha256.hmac(api.clientKey, hash)
                    
                    if(nonce !== false)
                        hash = sha256.hmac(api.clientKey, nonce+'-'+hash)
                    
                    options.headers['X-Signature'] = hash
                }
            }
            
            if(options.apiHeadersInUrl) {
                let isParamObject = $.type(options.params) === 'object'
                
                $.each(this.apiHeadersInUrl, (a, b) => {
                    if(typeof options.headers[a] != 'undefined') {
                        if(isParamObject)
                            options.params[b] = options.headers[a]
                        else
                            options.params += '&'+b+'='+encodeURIComponent(options.headers[a])
                        
                        delete options.headers[a]
                    }
                })
                
                if(!isParamObject)
                    options.params = options.params.replace(/^&/, '')
            }
            
            if(options.returnPayload)
                return wait.resolve(options).promise()
            
            if(this.onBeforeRequestCallback) {
                let result = this.onBeforeRequestCallback(options)
                
                if(result.cmd === 'resolve')
                    return wait.resolve(result.response).promise()
                
                if(result.cmd === 'reject')
                    return wait.reject(result.response).promise()
            }
            
            Vue.$http.request(options)
                .then((response) => {
                    if(format === 'msgpack' && response.data)
                        response.data = msgpack.decode(new Uint8Array(response.data))
                    
                    if(this.onResponseSuccessCallback)
                        this.onResponseSuccessCallback(response)
                    
                    if(typeof response.data['errorType'] != 'undefined')
                        return wait.reject(response).promise()
                    else
                        return wait.resolve(response).promise()
                })
                .catch((error) => {
                    if(typeof error !== 'object' || !error.response)
                        return wait.reject(error).promise()
                    
                    if(format === 'msgpack' && error.response.data)
                        error.response.data = msgpack.decode(new Uint8Array(error.response.data))
                    
                    if(
                        !internalReTry
                            &&
                        error.response.data
                            &&
                        typeof error.response.data.errorCode != 'undefined'
                            &&
                        error.response.data.errorCode === 'INVALID_NONCE_GIVEN'
                    ) {
                        this._run(method, path, data, nonceRequired, givenOptions, contentType, true).then(
                            (r) => {
                                return wait.resolve(r).promise()
                            },
                            (r) => {
                                return wait.reject(r).promise()
                            }
                        )
                    }
                    else {
                        if(this.onResponseErrorCallback)
                            this.onResponseErrorCallback(error.response)
                        
                        return wait.reject(error.response).promise()
                    }
                })
                .catch(() => {
                    // ignore
                })
        }).catch((response) => {
            return wait.reject(response).promise()
        })
        
        return wait.promise()
    }
}

export default api
